import React, { useMemo } from 'react';

import { AnalyticsContext } from '@atlaskit/analytics-next';
import { Box } from '@atlaskit/primitives/compiled';
import { ModalContextProvider } from '@atlassian/entry-point-modal-context/ModalContextProvider';
import { ModalTrigger } from '@atlassian/entry-point-modal-trigger';

import { useAnalytics } from '../../utils/analytics';
import {
	AVOID_DEACTIVATION_MODAL_ACTION_SUBJECT_ID,
	TAKE_ACTION_SOURCE_ID,
} from '../../utils/constants';
import { getHamsKey } from '../../utils/product-terms';

import { autoUpgradeAvoidDeactivationEntrypoint, type EntryPointParams } from './entrypoint';
import { type AvoidDecativationModalTriggerProps } from './types';

export function AutoUpgradeAvoidDeactivationModal({
	product,
	cloudId,
	touchpointId,
	children,
	onError,
	onClose,
	renderWithWrapperAnalytics = false,
}: AvoidDecativationModalTriggerProps): React.JSX.Element {
	const { createTakeActionClickEvent } = useAnalytics();

	const entryPointParams: EntryPointParams = useMemo(
		() => ({
			cloudId,
			hamsProductKey: getHamsKey(product),
			product,
			touchpointId,
			onClose,
			onError,
		}),
		[cloudId, product, onError, onClose, touchpointId],
	);

	const modalProps = useMemo(
		() => ({ onError, onClose, touchpointId }),
		[onError, onClose, touchpointId],
	);

	const fireClickAnalytics = () => {
		createTakeActionClickEvent(TAKE_ACTION_SOURCE_ID, { product, touchpointId });
	};

	const handleTriggerClick = (_event: React.MouseEvent) => {
		fireClickAnalytics();
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			fireClickAnalytics();
		}
	};

	/**
	 * renderWithWrapperAnalytics is used to determine if the children should be wrapped in a Box component
	 * or a button component. AkFlags render their Actions as buttons, so rendering a child as a button violates a11y rules in jira.
	 * If the child is not a button, we render it as a Box component to avoid this issue.
	 */
	return (
		<AnalyticsContext
			data={{
				attributes: {
					product,
					touchpointId: AVOID_DEACTIVATION_MODAL_ACTION_SUBJECT_ID,
				},
			}}
		>
			<ModalContextProvider>
				<ModalTrigger
					entryPoint={autoUpgradeAvoidDeactivationEntrypoint}
					entryPointParams={{ ...entryPointParams, ...modalProps }}
					onError={onError}
				>
					{({ ref }) =>
						renderWithWrapperAnalytics ? (
							<Box as="span" ref={ref}>
								{children}
							</Box>
						) : (
							<Box
								as="span"
								ref={ref}
								role="button"
								tabIndex={0}
								onClick={handleTriggerClick}
								onKeyDown={handleKeyDown}
							>
								{children}
							</Box>
						)
					}
				</ModalTrigger>
			</ModalContextProvider>
		</AnalyticsContext>
	);
}
